import React, { useEffect, useState } from 'react';
import { payrollAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

export const TaxSetupSection = () => {
  const [data, setData] = useState({ count: 0, employees_scanned: 0, basis: '', employees: [] });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadIncomplete = () => payrollAPI.taxSetupIncomplete().then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { loadIncomplete(); }, []);

  const runPreview = async () => {
    setBusy(true);
    try { const r = await payrollAPI.taxSetupAutofillPreview(); setPreview(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not build the preview'); }
    finally { setBusy(false); }
  };

  const applyFill = async () => {
    setSaving(true);
    try {
      const r = await payrollAPI.taxSetupAutofillApply();
      toast.success(`Saved ${r.data.saved} setup${r.data.saved === 1 ? '' : 's'}; ${r.data.skipped_existing} already set up, ${r.data.skipped_no_data} had no data`);
      setPreview(null);
      loadIncomplete();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'You need payroll import rights to save this');
    } finally { setSaving(false); }
  };

  const c = preview?.counts || {};

  return (
    <div className="space-y-6">
      <Card data-testid="netpay-tax-setup-autofill">
        <CardHeader>
          <CardTitle>Fill tax setup from records we already have</CardTitle>
          <CardDescription>
            A one-time fill: resident state and locality from the home address, work state from the employee's payroll tax state, and whether a federal W-4 and state withholding record already exist. Review the counts below, then a payroll import holder confirms — nothing is saved until you do, and setup someone already entered is never overwritten.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" data-testid="autofill-preview-btn" onClick={runPreview} disabled={busy}>
              {busy ? 'Checking…' : 'Preview the fill'}
            </Button>
            {preview && (
              <Button data-testid="autofill-apply-btn" onClick={applyFill} disabled={saving || !c.filled && !c.needs_review}>
                {saving ? 'Saving…' : 'Confirm and save'}
              </Button>
            )}
          </div>

          {preview && (
            <>
              <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="autofill-counts">
                <Badge variant="default" data-testid="autofill-filled">{c.filled || 0} ready to fill</Badge>
                <Badge variant="outline" data-testid="autofill-needs-review">{c.needs_review || 0} need review</Badge>
                <Badge variant="outline" data-testid="autofill-no-data">{c.no_data || 0} no data</Badge>
                <Badge variant="secondary" data-testid="autofill-already">{c.already_set_up || 0} already set up</Badge>
                <span className="text-muted-foreground">{preview.employees_scanned} active employees checked</span>
              </div>
              <div className="overflow-auto max-h-80 border rounded text-sm">
                <table className="w-full">
                  <thead><tr className="text-left text-xs text-muted-foreground"><th className="p-2">Emp #</th><th>Name</th><th>Status</th><th>Still needs</th></tr></thead>
                  <tbody>
                    {(preview.employees_needing_manual_setup || []).map((e) => (
                      <tr key={e.employee_id} className="border-t" data-testid={`autofill-manual-${e.employee_id}`}>
                        <td className="p-2">{e.employee_number}</td>
                        <td className="text-foreground">{e.name}</td>
                        <td><Badge variant="outline">{e.status}</Badge></td>
                        <td className="text-amber-700 dark:text-amber-400">{(e.missing || []).join(', ')}</td>
                      </tr>
                    ))}
                    {!preview.employees_needing_manual_setup?.length && (
                      <tr><td colSpan={4} className="p-3 text-muted-foreground">Everyone can be filled from existing data — nothing left for manual setup.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground">The list above is everyone that still needs manual setup after the fill (missing a home state or a W-4/state record). “Ready to fill” employees are saved when you confirm.</p>
            </>
          )}
        </CardContent>
      </Card>

      <Card data-testid="netpay-tax-setup">
        <CardHeader>
          <CardTitle>Employee tax setup — incomplete list</CardTitle>
          <CardDescription>Active employees missing a federal W-4 and/or a state withholding record for a jurisdiction they would be taxed in. These must be resolved before a real run.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
            <Badge variant={data.count ? 'destructive' : 'default'} data-testid="tax-setup-count">{data.count} incomplete</Badge>
            <Badge variant="outline" data-testid="tax-setup-scanned">{data.employees_scanned} active employees checked</Badge>
          </div>
          <p className="mb-3 text-xs text-muted-foreground" data-testid="tax-setup-basis">
            This count is <b>one row per employee</b> — not per employee-and-jurisdiction. An employee missing both their W-4 and a state record is still counted once. So {data.count} is the number of <b>employees</b> who need attention, out of {data.employees_scanned} active employees checked.
          </p>
          <div className="overflow-auto max-h-96 border rounded text-sm">
            <table className="w-full">
              <thead><tr className="text-left text-xs text-muted-foreground"><th className="p-2">Emp #</th><th>Name</th><th>Missing</th></tr></thead>
              <tbody>
                {(data.employees || []).map((e) => (
                  <tr key={e.employee_id} className="border-t" data-testid={`tax-setup-row-${e.employee_id}`}>
                    <td className="p-2">{e.employee_number}</td><td className="text-foreground">{e.name}</td>
                    <td className="text-amber-700 dark:text-amber-400">{(e.missing || []).join(', ')}</td>
                  </tr>
                ))}
                {!data.employees?.length && <tr><td colSpan={3} className="p-3 text-muted-foreground">All active employees have complete tax setup.</td></tr>}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
